import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { IPerformanceAssessmentEntryStudentView } from './performanceAssessmentEntryDetailDialog.types';

interface IPerformanceAssessmentEntryStudentSelectionComposable {
  initializeSelection: (pinnedStudentId?: number) => void;
  isOverview: ComputedRef<boolean>;
  selectedStudentId: Ref<number | null>;
  selectedStudentView: ComputedRef<IPerformanceAssessmentEntryStudentView | null>;
  selectStudent: (studentId: number | null) => void;
  selectRelativeStudent: (offset: -1 | 1) => void;
  visibleStudentViews: ComputedRef<IPerformanceAssessmentEntryStudentView[]>;
}

export const usePerformanceAssessmentEntryStudentSelection = (
  studentViews: Ref<IPerformanceAssessmentEntryStudentView[]>,
): IPerformanceAssessmentEntryStudentSelectionComposable => {
  const selectedStudentId = ref<number | null>(null);
  const studentIds = computed(() => studentViews.value.map(({ student }) => student.id));
  const defaultStudentId = computed(() => (studentIds.value.length === 1 ? studentIds.value[0] : null));
  const studentCycle = computed<(number | null)[]>(() =>
    studentIds.value.length > 1 ? [null, ...studentIds.value] : studentIds.value,
  );
  const isOverview = computed(() => studentIds.value.length > 1 && selectedStudentId.value === null);
  const selectedStudentView = computed(
    () => studentViews.value.find(({ student }) => student.id === selectedStudentId.value) ?? null,
  );
  const visibleStudentViews = computed(() => {
    if (isOverview.value) return studentViews.value;
    return selectedStudentView.value ? [selectedStudentView.value] : [];
  });

  const initializeSelection = (pinnedStudentId?: number): void => {
    selectedStudentId.value =
      pinnedStudentId != null && studentIds.value.includes(pinnedStudentId) ? pinnedStudentId : defaultStudentId.value;
  };

  watch(
    studentIds,
    (ids) => {
      if (selectedStudentId.value != null && ids.includes(selectedStudentId.value)) return;
      selectedStudentId.value = defaultStudentId.value;
    },
    { immediate: true, flush: 'sync' },
  );

  const selectStudent = (studentId: number | null): void => {
    if (!studentCycle.value.includes(studentId)) return;
    selectedStudentId.value = studentId;
  };

  const selectRelativeStudent = (offset: -1 | 1): void => {
    const cycle = studentCycle.value;
    if (cycle.length < 2) return;
    const currentIndex = Math.max(0, cycle.indexOf(selectedStudentId.value));
    selectStudent(cycle[(currentIndex + offset + cycle.length) % cycle.length]);
  };

  return {
    initializeSelection,
    isOverview,
    selectedStudentId,
    selectedStudentView,
    selectStudent,
    selectRelativeStudent,
    visibleStudentViews,
  };
};
